import Icon from '@/components/ui/AppIcon';

interface ContactMethod {
  id: string;
  title: string;
  description: string;
  icon: string;
  primary: string;
  secondary?: string;
  action: string;
  urgent?: boolean;
}

interface ContactMethodsProps {
  className?: string;
}

const ContactMethods = ({ className = '' }: ContactMethodsProps) => {
  const contactMethods: ContactMethod[] = [
    {
      id: 'emergency',
      title: 'Emergency Hotline',
      description: 'Facing foreclosure or urgent situation? Call now for immediate assistance.',
      icon: 'ExclamationTriangleIcon',
      primary: '(908) 897-0464',
      secondary: 'Available 24/7',
      action: 'Call Now',
      urgent: true,
    },
    {
      id: 'email',
      title: 'Email Support',
      description: 'Send detailed inquiries, documents, or questions via email.',
      icon: 'EnvelopeIcon',
      primary: 'Outreach@Silkhomesolutions.com',
      secondary: 'Response within 2 hours',
      action: 'Send Email',
    },
    {
      id: 'text',
      title: 'Text Messages',
      description: 'Quick questions or updates via SMS for faster communication.',
      icon: 'ChatBubbleLeftIcon',
      primary: '(908) 897-0464',
      secondary: 'Text-friendly responses',
      action: 'Send Text',
    },
  ];

  return (
    <section className={`py-16 lg:py-24 bg-background ${className}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Choose Your Preferred Contact Method
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We understand different situations require different communication styles. Select the
            method that works best for your needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {contactMethods.map((method) => (
            <div
              key={method.id}
              className={`relative p-6 bg-card rounded-lg border border-border silk-smooth hover:shadow-lg ${
                method.urgent ? 'ring-2 ring-error trust-glow' : ''
              }`}
            >
              {method.urgent && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-error text-error-foreground px-3 py-1 rounded-full text-xs font-medium">
                    URGENT
                  </span>
                </div>
              )}

              <div className="text-center">
                <div
                  className={`inline-flex p-3 rounded-full mb-4 ${
                    method.urgent ? 'bg-error/10' : 'bg-primary/10'
                  }`}
                >
                  <Icon
                    name={method.icon as any}
                    size={24}
                    className={method.urgent ? 'text-error' : 'text-primary'}
                  />
                </div>

                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {method.title}
                </h3>

                <p className="text-sm text-muted-foreground mb-4">{method.description}</p>

                <div className="space-y-2 mb-4">
                  <p className="text-base font-medium text-foreground">{method.primary}</p>
                  {method.secondary && (
                    <p className="text-xs text-muted-foreground">{method.secondary}</p>
                  )}
                </div>

                <button
                  className={`w-full px-4 py-2 rounded-md font-medium silk-smooth ${
                    method.urgent
                      ? 'bg-error text-error-foreground hover:bg-error/90'
                      : 'bg-primary text-primary-foreground hover:bg-primary/90'
                  }`}
                >
                  {method.action}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContactMethods;
